import { Button } from '@shared/components/Button';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { ASK_MARK_ICON } from '../components/AskMark';
import { useIsCaptureEnabled } from './useIsCaptureEnabled';
import { OpenCaptureParams, useOpenCapture } from './useOpenCapture';

/** Sized to sit beside the label in the same bar without out-weighing it. */
const MARK_ICON_SIZE = grid(2.75);

interface CaptureButtonProps extends OpenCaptureParams {
  /** Defaults to the in-form wording. The workspace header, where no form is open yet, passes its own. */
  tooltip?: string;
  /** For a bar whose segments are sized off the design system's default, as the header's is. */
  className?: string;
}

/**
 * Opens Capture, as the left segment of a bar whose right segment creates a card.
 *
 * Navigates to Capture's route rather than opening a modal it owns: the modal belongs to the create
 * form's route, so this button works the same from the workspace header (no form open yet) and from
 * the form's own title bar.
 *
 * Icon only, and no variant of its own: the bar sets one for every segment, and both of the bars this
 * sits in ask for the filled `primary` rather than the default tertiary, because two colours in one
 * bar read as two separate buttons that happen to touch. The mark alone carries the meaning — a
 * labelled "Capture" beside "Create" read as two competing verbs when only one of them makes a card.
 */
export function CaptureButton({ tooltip, className, ...params }: CaptureButtonProps) {
  const isEnabled = useIsCaptureEnabled();
  const openCapture = useOpenCapture(params);

  if (!isEnabled) {
    return null;
  }

  return (
    <AnchoredTooltip tooltip={tooltip ?? i18n('ask.capture.tooltip')} position="bottom">
      <Button
        icon={ASK_MARK_ICON}
        iconOnly
        iconSize={MARK_ICON_SIZE}
        label={i18n('ask.capture.title')}
        onClick={openCapture}
        className={className}
        testID="ask-capture-open"
      />
    </AnchoredTooltip>
  );
}
